import React, { useState, useEffect, useCallback } from 'react';
import {
    getPatientGroups,
    createPatientGroup,
    updatePatientGroup,
    deletePatientGroup,
    getGroupPatients,
    assignPatientToGroup,
    unassignPatientFromGroup,
    getUsers,
} from '../api/api';
import './PatientGroups.css';

const emptyGroup = () => ({
    _id: '',
    name: '',
    description: '',
});

const PatientGroups = () => {
    const [groups, setGroups] = useState([]);
    const [editing, setEditing] = useState(null); // null = list, object = form
    const [isNew, setIsNew] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Patients panel
    const [viewingGroup, setViewingGroup] = useState(null);
    const [patients, setPatients] = useState([]);
    const [assignEmail, setAssignEmail] = useState('');

    // Autocomplete State
    const [allUsers, setAllUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const g = await getPatientGroups();
            setGroups(g);
        } catch (err) {
            setError(err.message || 'Error cargando datos');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    // Fetch all users when opening a group view
    useEffect(() => {
        if (viewingGroup) {
            getUsers().then(users => {
                setAllUsers(users);
            }).catch(e => console.error("Error loading users for autocomplete", e));
        }
    }, [viewingGroup]);

    // ── Group CRUD helpers ────
    const handleNew = () => { setEditing(emptyGroup()); setIsNew(true); };
    const handleEdit = (g) => { setEditing(JSON.parse(JSON.stringify(g))); setIsNew(false); };
    const handleCancel = () => { setEditing(null); setIsNew(false); setError(''); };

    const handleDelete = async (id) => {
        if (!window.confirm(`¿Eliminar el grupo "${id}"?`)) return;
        try { await deletePatientGroup(id); await loadData(); } catch (e) { setError(e.message); }
    };

    const handleSave = async () => {
        if (!editing._id?.trim()) { setError('El ID es obligatorio'); return; }
        if (!editing.name?.trim()) { setError('El nombre es obligatorio'); return; }

        setSaving(true); setError('');
        try {
            if (isNew) { await createPatientGroup(editing); } else { await updatePatientGroup(editing._id, editing); }
            setEditing(null); setIsNew(false); await loadData();
        } catch (e) { setError(e.message); } finally { setSaving(false); }
    };

    // ── Patients panel ────
    const openPatients = async (group) => {
        setViewingGroup(group);
        setSearchTerm('');
        setAssignEmail('');
        try {
            const p = await getGroupPatients(group._id);
            setPatients(p);
        } catch (e) { setError(e.message); }
    };

    const closePatients = () => {
        setViewingGroup(null);
        setPatients([]);
        setAssignEmail('');
        setSearchTerm('');
    };

    const handleAssign = async () => {
        if (!assignEmail.trim()) return;
        try {
            await assignPatientToGroup(viewingGroup._id, assignEmail.trim());
            setAssignEmail('');
            setSearchTerm('');
            const p = await getGroupPatients(viewingGroup._id);
            setPatients(p);
        } catch (e) { setError(e.message); }
    };

    const handleUnassign = async (patientId) => {
        try {
            await unassignPatientFromGroup(viewingGroup._id, patientId);
            const p = await getGroupPatients(viewingGroup._id);
            setPatients(p);
        } catch (e) { setError(e.message); }
    };

    // Autocomplete Handlers
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        setAssignEmail(value); // Allow manual entry as fallback

        if (value.length > 1) {
            const lowerVal = value.toLowerCase();
            const filtered = allUsers.filter(u =>
                (u.email.toLowerCase().includes(lowerVal) ||
                    (u.first_name + ' ' + u.last_name).toLowerCase().includes(lowerVal)) &&
                !patients.some(p => p.patient_id === u.email)
            );
            setSuggestions(filtered.slice(0, 5));
            setShowDropdown(true);
        } else {
            setShowDropdown(false);
        }
    };

    const selectUser = (user) => {
        setAssignEmail(user.email);
        setSearchTerm(`${user.first_name} ${user.last_name} (${user.email})`);
        setShowDropdown(false);
    };

    const handleAssignWrapper = async () => {
        await handleAssign();
    };

    // ── Patients overlay ────
    if (viewingGroup) {
        return (
            <div className="patient-groups-page">
                <div className="page-header">
                    <h1>👥 Pacientes de: {viewingGroup.name}</h1>
                    <button onClick={closePatients} className="btn-secondary">← Volver</button>
                </div>
                {error && <div className="error-message">❌ {error}</div>}

                <div className="assign-bar">
                    <div style={{ flex: 1, position: 'relative' }}>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            onFocus={() => searchTerm.length > 1 && setShowDropdown(true)}
                            onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                            placeholder="Buscar paciente por nombre o email..."
                            className="autocomplete-input"
                        />
                        {showDropdown && (
                            <div className="autocomplete-dropdown">
                                {suggestions.map(u => (
                                    <div key={u.email} className="autocomplete-item" onClick={() => selectUser(u)}>
                                        <strong>{u.first_name} {u.last_name}</strong>
                                        <small>{u.email}</small>
                                    </div>
                                ))}
                                {suggestions.length === 0 && (
                                    <div className="autocomplete-item disabled">No se encontraron coincidencias</div>
                                )}
                            </div>
                        )}
                    </div>
                    <button onClick={handleAssignWrapper} className="btn-primary">➕ Asignar</button>
                </div>

                {patients.length === 0 ? (
                    <div className="empty-state"><p>No hay pacientes asignados a este grupo.</p></div>
                ) : (
                    <div className="table-responsive">
                        <table className="groups-table">
                            <thead>
                                <tr>
                                    <th>Paciente</th>
                                    <th>Asignado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {patients.map((p) => (
                                    <tr key={p.patient_id}>
                                        <td className="mono">{p.patient_id}</td>
                                        <td>{new Date(p.assigned_at).toLocaleDateString('es-MX')}</td>
                                        <td>
                                            <button onClick={() => handleUnassign(p.patient_id)} className="btn-danger btn-sm">Quitar</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    }

    // ── LIST VIEW ────
    if (!editing) {
        return (
            <div className="patient-groups-page">
                <div className="page-header">
                    <h1 style={{ margin: 0, fontSize: '1.4rem' }}>🏥 Grupos de Pacientes</h1>
                    <button onClick={handleNew} className="btn-primary">➕ Nuevo Grupo</button>
                </div>
                {error && <div className="error-message">❌ {error}</div>}

                {loading ? (
                    <p className="loading-text">Cargando grupos...</p>
                ) : groups.length === 0 ? (
                    <div className="empty-state">
                        <p>No hay grupos creados todavía.</p>
                        <button onClick={handleNew} className="btn-primary">Crear el primero</button>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="groups-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Nombre</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groups.map((g) => (
                                    <tr key={g._id}>
                                        <td className="mono">{g._id}</td>
                                        <td>{g.name}</td>
                                        <td>
                                            <div className="actions-cell">
                                                <button onClick={() => openPatients(g)} className="btn-secondary btn-sm">👥 Pacientes</button>
                                                <button onClick={() => handleEdit(g)} className="btn-secondary btn-sm">✏️ Editar</button>
                                                <button onClick={() => handleDelete(g._id)} className="btn-danger btn-sm">🗑️</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    }

    // ── FORM VIEW ────
    return (
        <div className="patient-groups-page">
            <div className="page-header">
                <h1>{isNew ? '✨ Nuevo Grupo' : `✏️ Editar: ${editing.name || editing._id}`}</h1>
                <div>
                    <button onClick={handleCancel} className="btn-secondary">Cancelar</button>
                    <button onClick={handleSave} className="btn-primary" disabled={saving}>
                        {saving ? '⌛ Guardando...' : '💾 Guardar'}
                    </button>
                </div>
            </div>
            {error && <div className="error-message">❌ {error}</div>}

            <div className="form-section">
                <h2>Datos principales</h2>
                <div className="form-row">
                    <div className="form-group flex-1">
                        <label>Identificador (Slug)</label>
                        <input type="text" value={editing._id} disabled={!isNew}
                            onChange={(e) => setEditing({ ...editing, _id: e.target.value.replace(/\s/g, '_').toLowerCase() })}
                            placeholder="ej: cancer_mama" />
                    </div>
                    <div className="form-group flex-2">
                        <label>Nombre del Grupo</label>
                        <input type="text" value={editing.name}
                            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                            placeholder="Nombre visible para internos" />
                    </div>
                </div>
                <div className="form-group">
                    <label>Descripción</label>
                    <textarea value={editing.description || ''} rows={3}
                        onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                        placeholder="Agrega notas o detalles sobre este grupo de pacientes..." />
                </div>
            </div>
        </div>
    );
};

export default PatientGroups;
